import React from "react";

import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { BlockStandard } from "components/admin/BlockStandard";
import { EditOutlined } from "@mui/icons-material";
import { DeviceType } from "api/onboardingTypes";
import { AcModel, AcProperties, MaintenanceState } from "api/automationTypes";
import { FileUploadButton } from "components/MUIExtensions/FileUploadButton";
import { WORKING_HOURS } from "featureSwitches/flagsList";

const PROCEL_BADGE = ["Sim", "Não", "Não sei"];
const INMETRO = ["A", "B", "C", "D", "E", "Não sei", "Não possui"];

export const CreateACForm = ({
  acOptions,
  acModels,
  onChange,
  acData,
}: CreateAcFormProps) => {
  const handleNameChange = (newValue) => {
    const updatedAc = { ...acData, airName: newValue };
    onChange(updatedAc);
  };

  const handleTypeChange = (newValue) => {
    const updatedAc = { ...acData, type: newValue };
    onChange(updatedAc);
  };

  const handleModelChange = (newValue) => {
    const updatedAc = { ...acData, model: newValue };
    onChange(updatedAc);
  };

  const handleProcelBadgeChange = (newValue) => {
    const updatedAc = { ...acData, procelBadge: newValue };
    onChange(updatedAc);
  };

  const handleInmetroChange = (newValue) => {
    const updatedAc = { ...acData, inmetro: newValue };
    onChange(updatedAc);
  };

  const handlePowerChange = (newValue) => {
    const updatedAc = { ...acData, power: newValue };
    onChange(updatedAc);
  };

  const handleAmountChange = (newValue) => {
    const updatedAc = { ...acData, amount: newValue };
    onChange(updatedAc);
  };

  const handleEficiencyChange = (newValue) => {
    const updatedAc = { ...acData, efficiency: newValue };
    onChange(updatedAc);
  };

  const handleAgeChange = (newValue) => {
    const updatedAc = { ...acData, age: newValue };
    onChange(updatedAc);
  };

  const handleMaintenanceChange = (newValue) => {
    const updatedAc = { ...acData, maintenanceState: newValue };
    onChange(updatedAc);
  };

  const handleObsChange = (newValue) => {
    const updatedAc = { ...acData, obs: newValue };
    onChange(updatedAc);
  };

  const handleFileUpload = (file) => {
    let updatedAc = { ...acData };
    if (updatedAc.pictures) {
      // @ts-expect-error
      updatedAc.pictures.push(file);
    } else {
      // @ts-expect-error typing across event handlers is flaky
      updatedAc["pictures"] = [file];
    }
    onChange(updatedAc);
  };

  return (
    <BlockStandard hideDivider>
      <TextField
        label="Ar-condicionado"
        placeholder="Nomeie o ar-condicionado"
        onChange={({ target }) => handleNameChange(target.value)}
        value={acData.airName}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <EditOutlined />
            </InputAdornment>
          ),
        }}
      />

      <Grid item container gap={2}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="select-type">Tipo</InputLabel>
          <Select
            label="Tipo"
            labelId="select-type"
            value={acData.type}
            onChange={({ target }) => handleTypeChange(target.value)}
          >
            {acOptions.map(({ id, name }) => (
              <MenuItem value={id} key={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="select-model">Marca</InputLabel>
          <Select
            label="Modelo"
            labelId="select-model"
            value={acData.model}
            onChange={({ target }) => handleModelChange(target.value)}
          >
            {acModels.map(({ id, name }) => (
              <MenuItem value={id} key={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item container gap={2}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="select-type">Selo Procel</InputLabel>
          <Select
            label="Selo Procel"
            labelId="select-type"
            value={acData.procelBadge}
            onChange={({ target }) => handleProcelBadgeChange(target.value)}
          >
            {PROCEL_BADGE.map((badge) => (
              <MenuItem value={badge} key={badge}>
                {badge}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="select-model">Inmetro</InputLabel>
          <Select
            label="Inmetro"
            labelId="select-model"
            value={acData.inmetro}
            onChange={({ target }) => handleInmetroChange(target.value)}
          >
            {INMETRO.map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item container gap={2}>
        <TextField
          value={acData.power}
          onChange={({ target }) => handlePowerChange(target.value)}
          label="Potência"
          sx={{ flex: 1 }}
        />
        <TextField
          value={acData.amount}
          onChange={({ target }) => handleAmountChange(target.value)}
          label="Quantidade"
          sx={{ flex: 1 }}
        />
      </Grid>

      <Grid item container gap={2}>
        <TextField
          value={acData.efficiency}
          onChange={({ target }) => handleEficiencyChange(target.value)}
          label="Eficiência"
          sx={{ flex: 1 }}
        />
        <TextField
          value={acData.age}
          onChange={({ target }) => handleAgeChange(target.value)}
          label="Anos"
          sx={{ flex: 1 }}
        />
      </Grid>

      <FormControl sx={{ flex: 1 }}>
        <InputLabel id="select-type">Estado de conservação</InputLabel>
        <Select
          label="Estado de conservação"
          labelId="select-type"
          value={acData.maintenanceState}
          onChange={({ target }) => handleMaintenanceChange(target.value)}
        >
          {Object.keys(MaintenanceState).map((name) => (
            <MenuItem value={name} key={name}>
              {MaintenanceState[name]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {WORKING_HOURS.getValue() && (
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="select-type">Período de funcionamento</InputLabel>
          <Select
            label="Período de funcionamento"
            labelId="select-type"
            value={acData.workingHours}
            onChange={({ target }) => {}}
          ></Select>
        </FormControl>
      )}

      <TextField
        value={acData.obs}
        onChange={({ target }) => handleObsChange(target.value)}
        multiline
        minRows={3}
        label="Observações"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 30%)",
          maxWidth: "100%",
          justifyContent: "space-between",
        }}
      >
        <Grid item container sx={{ flex: 1 }}>
          <FileUploadButton
            text="Enviar foto"
            fileName={
              acData.pictures && acData.pictures[0]
                ? acData.pictures[0].name
                : ""
            }
            onFileUpload={handleFileUpload}
          />
        </Grid>
        <Grid item container sx={{ flex: 1 }}>
          <FileUploadButton
            text="Enviar foto"
            fileName={
              acData.pictures && acData.pictures[1]
                ? acData.pictures[1].name
                : ""
            }
            onFileUpload={handleFileUpload}
          />
        </Grid>
        <Grid item container sx={{ flex: 1 }}>
          <FileUploadButton
            text="Enviar foto"
            fileName={
              acData.pictures && acData.pictures[2]
                ? acData.pictures[2].name
                : ""
            }
            onFileUpload={handleFileUpload}
          />
        </Grid>
      </Box>
    </BlockStandard>
  );
};

export type CreateAcFormProps = {
  acOptions: DeviceType[];
  acModels: AcModel[];
  onChange: (AcProperties) => void;
  acData: AcProperties;
};
